import { FC } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  CreditCard,
  FileText,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  useTherapistAppointmentById,
  useUpdateAppointmentStatus,
} from '../../hooks/useTherapistDashboard';
import { useToast } from '../../components/ui/Toast';
import { formatCurrency } from '../../utils/currency';
import { cn } from '../../utils/cn';
import type { AppointmentStatus } from '../../types/therapist-dashboard.types';

// Status badge configuration
const statusConfig: Record<AppointmentStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  confirmed: { label: 'Confirmed', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  completed: { label: 'Completed', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  no_show: { label: 'No Show', className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
};

// Payment status badge configuration
const paymentStatusConfig: Record<string, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const StatusBadge: FC<{ status: AppointmentStatus }> = ({ status }) => {
  const config = statusConfig[status] || statusConfig.pending;
  return (
    <span className={cn('px-3 py-1 text-sm font-medium rounded-full', config.className)}>
      {config.label}
    </span>
  );
};

const PaymentBadge: FC<{ status: string }> = ({ status }) => {
  const config = paymentStatusConfig[status] ?? paymentStatusConfig['pending'];
  return (
    <span className={cn('px-3 py-1 text-sm font-medium rounded-full', config!.className)}>
      {config!.label}
    </span>
  );
};

const InfoRow: FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-slate-700 last:border-0">
    <div className="text-gray-400 dark:text-slate-500 mt-0.5">{icon}</div>
    <div className="flex-1">
      <span className="text-sm text-gray-500 dark:text-slate-400 block">{label}</span>
      <span className="font-medium text-gray-900 dark:text-slate-100">{value}</span>
    </div>
  </div>
);

export const TherapistAppointmentDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { data: appointment, isLoading, error, isNotFound, refetch } = useTherapistAppointmentById(id || '');
  const updateStatusMutation = useUpdateAppointmentStatus();
  const { showToast } = useToast();

  // Check if appointment can be marked as completed or no-show
  const appointmentTime = appointment ? new Date(appointment.startTime) : null;
  const now = new Date();
  const canUpdateStatus =
    appointment?.status === 'confirmed' && appointmentTime && appointmentTime < now;

  const handleMarkCompleted = () => {
    if (!id) return;
    updateStatusMutation.mutate(
      { appointmentId: id, status: 'completed' },
      {
        onSuccess: () => {
          showToast({ type: 'success', message: 'Appointment marked as completed' });
          refetch();
        },
        onError: () => {
          showToast({ type: 'error', message: 'Failed to update appointment status' });
        },
      }
    );
  };

  const handleMarkNoShow = () => {
    if (!id) return;
    updateStatusMutation.mutate(
      { appointmentId: id, status: 'no_show' },
      {
        onSuccess: () => {
          showToast({ type: 'success', message: 'Appointment marked as no-show' });
          refetch();
        },
        onError: () => {
          showToast({ type: 'error', message: 'Failed to update appointment status' });
        },
      }
    );
  };

  // Auth loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Not a therapist
  if (user.role !== 'THERAPIST') {
    return <Navigate to="/dashboard" replace />;
  }

  // Loading appointment
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-400">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  // Error or not found
  if (error || isNotFound || !appointment) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Appointment Not Found
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            {error instanceof Error
              ? error.message
              : 'The appointment you are looking for does not exist.'}
          </p>
          <Link
            to="/therapist/dashboard"
            className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Link */}
        <Link
          to="/therapist/dashboard"
          className="inline-flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </Link>

        {/* Main Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold mb-2">{appointment.serviceName}</h1>
                <p className="text-blue-100">{appointment.duration} minutes</p>
              </div>
              <StatusBadge status={appointment.status} />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Client Information */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Client Information
              </h2>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                <InfoRow
                  icon={<User className="w-5 h-5" />}
                  label="Name"
                  value={
                    <span className="flex items-center gap-2">
                      {appointment.clientName}
                      {appointment.isNewClient && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                          New Client
                        </span>
                      )}
                    </span>
                  }
                />
                <InfoRow
                  icon={<Mail className="w-5 h-5" />}
                  label="Email"
                  value={appointment.clientEmail}
                />
                {appointment.clientPhone && (
                  <InfoRow
                    icon={<Phone className="w-5 h-5" />}
                    label="Phone"
                    value={appointment.clientPhone}
                  />
                )}
              </div>
            </div>

            {/* Appointment Details */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Appointment Details
              </h2>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                <InfoRow
                  icon={<Calendar className="w-5 h-5" />}
                  label="Date"
                  value={formatDate(appointment.startTime)}
                />
                <InfoRow
                  icon={<Clock className="w-5 h-5" />}
                  label="Time"
                  value={`${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
                />
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Payment Information
              </h2>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
                <InfoRow
                  icon={<CreditCard className="w-5 h-5" />}
                  label="Amount"
                  value={formatCurrency(appointment.amount)}
                />
                <div className="flex items-center gap-3 py-3">
                  <div className="text-gray-400 dark:text-slate-500">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-gray-500 dark:text-slate-400 block">Status</span>
                    <PaymentBadge status={appointment.paymentStatus} />
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {(appointment.clientNotes || appointment.notes) && (
              <div>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                  Notes
                </h2>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-4">
                  {appointment.clientNotes && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                          Customer Notes
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-slate-400 italic pl-6">
                        "{appointment.clientNotes}"
                      </p>
                    </div>
                  )}
                  {appointment.notes && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                          Therapist Notes
                        </span>
                      </div>
                      <p className="text-gray-600 dark:text-slate-400 pl-6">{appointment.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Booking Reference */}
            <div className="text-xs text-gray-400 dark:text-slate-500">
              Booking #{appointment.bookingNumber}
            </div>

            {/* Actions */}
            {canUpdateStatus && (
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
                <button
                  onClick={handleMarkCompleted}
                  disabled={updateStatusMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-5 h-5" />
                  {updateStatusMutation.isPending ? 'Updating...' : 'Mark as Completed'}
                </button>
                <button
                  onClick={handleMarkNoShow}
                  disabled={updateStatusMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle className="w-5 h-5" />
                  {updateStatusMutation.isPending ? 'Updating...' : 'Mark as No-Show'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistAppointmentDetailPage;
